(() => {
  if (customElements.get('wallify-personalization-order')) return;
  customElements.define('wallify-personalization-order', class extends HTMLElement {
    static values = new Map();
    static artworkLoads = new Map();
    static imageDecodes = new WeakMap();
    static preparedArtworks = new WeakMap();
    static loadingOwners = new WeakMap();
    connectedCallback() {
      if (this.controller) return;
      const node = this.querySelector('script[type="application/vnd.wallify-personalization+json"]');
      let config;
      try { config = JSON.parse(node?.textContent || ''); } catch { return; }
      const root = this.closest('.product');
      if (!root || root.querySelector('wallify-design-thumbnails') || !config.formId || !config.fields?.length) return;
      this.config = config;
      this.root = root;
      this.key = config.productId + '|' + config.formId;
      this.records = new Map();
      this.seen = new WeakSet();
      this.loadingSurfaces = new Map();
      this.previewToken = (this.previewToken || 0) + 1;
      this.previewSignature = null;
      this.previewPending = null;
      this.previewNavigate = false;
      this.posterToken = (this.posterToken || 0) + 1;
      this.posterSignature = null;
      this.posterPending = null;
      this.posterNavigate = false;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.origin = this.origin?.isConnected ? this.origin : document.createComment('wallify-personalization-origin');
      if (!this.origin.isConnected) this.before(this.origin);
      this.addEventListener('input', event => this.onPropertyEvent(event), { signal });
      this.addEventListener('change', event => this.onPropertyEvent(event), { signal });
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closeMenus();
      }, { signal });
      document.getElementById(config.formId)?.addEventListener('reset', () => {
        setTimeout(() => {
          if (!this.isConnected) return;
          this.records.forEach(record => { this.remember(record); this.syncMenu(record); });
          this.closeMenus();
          this.updateDesignPreview();
        }, 0);
      }, { signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(root, { childList: true, subtree: true });
      root.addEventListener('change', event => {
        if (event.target.closest?.('variant-selects')?.closest('.product') !== root) return;
        this.previewNavigate = false;
        this.posterNavigate = false;
        this.schedule();
      }, { capture: true, signal });
      root.addEventListener('wallify:premium-change', () => this.schedule(), { signal });
      root.addEventListener('click', event => {
        if (!event.target.closest?.('[data-wallify-design-loading="true"]') || !event.target.closest?.('a[data-pswp-src]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true, signal });
      this.attach();
    }
    schedule() {
      if (this.task) return;
      this.task = requestAnimationFrame(() => {
        this.task = null;
        if (this.isConnected) this.attach();
      });
    }
    findField(spec) {
      const form = document.getElementById(this.config.formId);
      const direct = document.getElementById(spec.id);
      const control = direct?.form === form ? direct : Array.from(this.root.querySelectorAll('.product__options input, .product__options textarea, .product__options select')).find(input => input.name === spec.name && input.form === form);
      const field = control?.closest('.product__options');
      if (!field || field.closest('.product') !== this.root) return null;
      const controls = Array.from(field.querySelectorAll('input, textarea, select')).filter(input => input.form === form && input.name === spec.name);
      return { field, controls, select: controls.find(input => input instanceof HTMLSelectElement) };
    }
    attach() {
      const picker = this.root.querySelector('variant-selects');
      const groups = picker ? Array.from(picker.children).filter(child => child.classList.contains('product-form__input')) : [];
      const color = groups[2];
      if (!color) return;
      const ordered = [];
      for (const spec of this.config.fields) {
        const found = this.findField(spec);
        if (!found || found.select && picker.contains(found.select)) continue;
        if (found.select) this.ensureDesignOptions(found.select, spec);
        let record = this.records.get(spec.id);
        if (!record || record.field !== found.field || record.controls.length !== found.controls.length || record.controls.some((control, index) => control !== found.controls[index])) {
          const previous = record;
          if (previous?.select) previous.view.remove();
          record = { ...found, spec };
          record.controls.forEach((control, index) => {
            if (this.seen.has(control)) return;
            const saved = this.constructor.values.get(this.key + '|' + spec.id + '|' + index);
            if (saved) {
              control.value = saved.value;
              if ('checked' in control) control.checked = saved.checked;
            }
            this.seen.add(control);
            control.addEventListener('input', () => { this.remember(record); this.syncMenu(record); this.updateDesignPreview(); }, { signal: this.controller.signal });
            control.addEventListener('change', () => { this.remember(record); this.syncMenu(record); this.updateDesignPreview(); }, { signal: this.controller.signal });
          });
          this.remember(record);
          if (record.select) {
            record.wasHidden = previous?.field === record.field ? previous.wasHidden : record.field.hidden;
            record.view = this.createMenu(record);
            record.select.addEventListener('invalid', event => {
              event.preventDefault();
              record.trigger.setAttribute('aria-invalid', 'true');
              record.list.setAttribute('aria-invalid', 'true');
              record.error.textContent = record.select.validationMessage;
              record.error.hidden = false;
              this.focusMenu(record);
            }, { signal: this.controller.signal });
          } else {
            record.anchor = previous?.field === record.field && previous.anchor?.isConnected ? previous.anchor : document.createComment('wallify-personalization-field');
            if (!record.anchor.isConnected) record.field.before(record.anchor);
            record.view = record.field;
          }
          this.records.set(spec.id, record);
        }
        if (record.select) {
          record.field.classList.add('wallify-personalization-native-field');
          record.field.hidden = true;
          this.syncMenu(record);
        }
        ordered.push(record.view);
      }
      if (!ordered.length) return;
      this.relocating = true;
      try {
        ordered.forEach((view, index) => {
          const existing = Array.from(this.children).filter(child => child.tagName !== 'SCRIPT')[index];
          if (existing !== view) this.insertBefore(view, existing || null);
        });
        if (this.parentElement !== picker || this.previousElementSibling !== color) color.after(this);
      } finally { this.relocating = false; }
      this.hidden = false;
      this.bindGalleryNavigation();
      this.prepareDesignChoices();
      this.updateDesignPreview();
    }
    bindGalleryNavigation() {
      const main = this.root.querySelector('gallery-carousel')?.main;
      if (!main || main === this.galleryMain) return;
      if (this.galleryMain && this.galleryMoveHandler) this.galleryMain.off('move.wallifyPersonalization');
      this.galleryMain = main;
      this.galleryMoveHandler = () => {
        if (!this.previewPending && !this.posterPending) return;
        this.previewNavigate = false;
        this.posterNavigate = false;
      };
      main.on('move.wallifyPersonalization', this.galleryMoveHandler);
    }
    remember(record) {
      record.controls.forEach((control, index) => {
        this.constructor.values.set(this.key + '|' + record.spec.id + '|' + index, { value: control.value, checked: control.checked });
      });
    }
    ensureDesignOptions(select, spec) {
      if (!Array.isArray(spec.designOptions)) return;
      const values = new Set(Array.from(select.options, option => option.value));
      spec.designOptions.forEach(value => {
        if (typeof value !== 'string' || !value.trim() || values.has(value)) return;
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.append(option);
        values.add(value);
      });
    }
    onPropertyEvent(event) {
      const control = event.target;
      if (!control?.name?.startsWith('properties[') || control.form?.id !== this.config.formId) return;
      this.records.forEach(record => { if (record.controls.includes(control)) this.remember(record); });
      if (event.type === 'change') {
        event.stopPropagation();
        this.root.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    createMenu(record) {
      const view = document.createElement('div');
      view.className = 'product__options product__info-inner';
      const field = document.createElement('div');
      field.className = 'field flex flex-col';
      const originalLabel = Array.from(record.select.labels || [])[0] || record.field.querySelector('label');
      const label = originalLabel?.cloneNode(true);
      const labelId = record.spec.id + '-personalization-label';
      if (label) {
        label.removeAttribute('for');
        label.id = labelId;
        field.append(label);
      }
      const menu = document.createElement('div');
      menu.className = 'wallify-personalization-menu';
      record.designGrid = Array.from(record.select.options).some(option => this.artworkFor(record, option.value) || this.posterMockupFor(record, option.value));
      if (record.designGrid) menu.classList.add('wallify-personalization-menu--design');
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'wallify-personalization-menu__trigger';
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', record.spec.id + '-personalization-list');
      if (label) trigger.setAttribute('aria-labelledby', labelId + ' ' + record.spec.id + '-personalization-value');
      const value = document.createElement('span');
      value.id = record.spec.id + '-personalization-value';
      trigger.append(value);
      const list = document.createElement('div');
      list.id = record.spec.id + '-personalization-list';
      list.className = 'wallify-personalization-menu__list';
      if (record.designGrid) list.classList.add('wallify-personalization-design-grid');
      list.setAttribute('role', 'listbox');
      if (label) list.setAttribute('aria-labelledby', labelId);
      list.hidden = !record.designGrid;
      const error = document.createElement('span');
      error.className = 'wallify-personalization-menu__error';
      error.id = record.spec.id + '-personalization-error';
      error.setAttribute('role', 'alert');
      error.hidden = true;
      trigger.setAttribute('aria-describedby', error.id);
      list.setAttribute('aria-describedby', error.id);
      if (record.designGrid) trigger.hidden = true;
      menu.append(trigger, list);
      field.append(menu, error);
      view.append(field);
      Object.assign(record, { trigger, value, list, error });
      const signal = this.controller.signal;
      trigger.addEventListener('click', () => {
        if (list.hidden) this.openMenu(record); else this.closeMenu(record);
      }, { signal });
      trigger.addEventListener('keydown', event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.openMenu(record, event.key);
      }, { signal });
      list.addEventListener('keydown', event => {
        const buttons = Array.from(list.children).filter(button => !button.disabled);
        const index = buttons.indexOf(event.target);
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeMenu(record);
          this.focusMenu(record);
        } else if (event.key === 'Tab') this.closeMenu(record);
        else if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + step + buttons.length) % buttons.length;
          buttons[next]?.focus();
        }
      }, { signal });
      return view;
    }
    syncMenu(record) {
      if (!record.select || !record.trigger) return;
      const options = Array.from(record.select.options);
      if (record.list.children.length !== options.length || Array.from(record.list.children).some((button, index) => button.textContent !== options[index].textContent)) {
        record.list.replaceChildren(...options.map((option, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'wallify-personalization-menu__option';
          button.setAttribute('role', 'option');
          button.tabIndex = -1;
          if (record.designGrid) {
            button.classList.add('wallify-personalization-design-card');
            const artwork = this.artworkFor(record, option.value) || this.posterMockupFor(record, option.value);
            if (artwork) {
              const art = document.createElement('span');
              art.className = 'wallify-personalization-design-card__art';
              const image = document.createElement('img');
              image.src = artwork.thumbnailSrc || artwork.src;
              image.width = artwork.width;
              image.height = artwork.height;
              image.alt = '';
              image.loading = 'lazy';
              image.decoding = 'async';
              image.addEventListener('error', () => { art.hidden = true; }, { once: true, signal: this.controller.signal });
              art.append(image);
              button.append(art);
            }
            const caption = document.createElement('span');
            caption.className = 'wallify-personalization-design-card__caption';
            caption.textContent = option.textContent;
            button.append(caption);
          } else button.textContent = option.textContent;
          const prepare = () => this.prepareDesign(record, option.value);
          button.addEventListener('pointerenter', prepare, { signal: this.controller.signal });
          button.addEventListener('focus', prepare, { signal: this.controller.signal });
          button.addEventListener('click', async () => {
            const token = this.designSelectionToken = (this.designSelectionToken || 0) + 1;
            const prepared = this.prepareDesign(record, option.value);
            if (prepared.status !== 'ready' && !await prepared.promise) return;
            if (!this.isConnected || token !== this.designSelectionToken) return;
            if (record.select.disabled || record.select.options[index].disabled) return;
            record.select.selectedIndex = index;
            record.select.dispatchEvent(new Event('input', { bubbles: true }));
            record.select.dispatchEvent(new Event('change', { bubbles: true }));
            this.syncMenu(record);
            this.closeMenu(record);
            this.updateDesignPreview({ navigate: true });
            if (record.designGrid) button.focus(); else record.trigger.focus();
          }, { signal: this.controller.signal });
          return button;
        }));
      }
      const text = record.select.selectedOptions[0]?.textContent || '';
      if (record.value.textContent !== text) record.value.textContent = text;
      record.trigger.disabled = record.select.disabled;
      record.trigger.setAttribute('aria-required', String(record.select.required));
      record.list.setAttribute('aria-required', String(record.select.required));
      const focusIndex = options[record.select.selectedIndex] && !options[record.select.selectedIndex].disabled ? record.select.selectedIndex : options.findIndex(option => !option.disabled);
      Array.from(record.list.children).forEach((button, index) => {
        button.disabled = record.select.disabled || options[index].disabled;
        button.setAttribute('aria-selected', String(index === record.select.selectedIndex));
        if (record.designGrid) button.tabIndex = !button.disabled && index === focusIndex ? 0 : -1;
      });
      if (record.select.validity.valid) {
        record.trigger.removeAttribute('aria-invalid');
        record.list.removeAttribute('aria-invalid');
        record.error.hidden = true;
      }
    }
    openMenu(record, key) {
      if (record.trigger.disabled) return;
      this.closeMenus();
      record.list.hidden = false;
      record.trigger.setAttribute('aria-expanded', 'true');
      const options = Array.from(record.list.children).filter(button => !button.disabled);
      const selected = record.list.children[record.select.selectedIndex];
      const target = key === 'Home' ? options[0] : key === 'End' ? options.at(-1) : !selected?.disabled ? selected : key === 'ArrowUp' ? options.at(-1) : options[0];
      target?.focus();
    }
    closeMenu(record) {
      if (!record.list) return;
      if (record.designGrid) return;
      record.list.hidden = true;
      record.trigger.setAttribute('aria-expanded', 'false');
    }
    closeMenus() {
      this.records.forEach(record => this.closeMenu(record));
    }
    focusMenu(record) {
      if (!record.designGrid) { record.trigger.focus(); return; }
      const selected = record.list.children[record.select.selectedIndex];
      const target = !selected?.disabled ? selected : Array.from(record.list.children).find(button => !button.disabled);
      target?.focus();
    }
    artworkFor(record, value) {
      return this.validImage(record.spec.designArtworks?.[value]);
    }
    posterMockupFor(record, value) {
      return this.validImage(record.spec.posterMockups?.[value]);
    }
    validImage(artwork) {
      if (!artwork || typeof artwork.src !== 'string' || !artwork.src.trim() || !Number.isFinite(Number(artwork.width)) || !Number.isFinite(Number(artwork.height)) || Number(artwork.width) <= 0 || Number(artwork.height) <= 0) return null;
      return { ...artwork, width: Number(artwork.width), height: Number(artwork.height) };
    }
    framedSelected() {
      const picker = this.root.querySelector('variant-selects');
      const groups = Array.from(picker?.children || []).filter(child => child.classList.contains('product-form__input'));
      const position = group => {
        const select = group?.querySelector('select');
        return select ? select.selectedIndex : Array.from(group?.querySelectorAll('input[type="radio"]') || []).findIndex(input => input.checked);
      };
      return position(groups[0]) > 0 && position(groups[2]) > 0;
    }
    posterSelected() {
      const picker = this.root.querySelector('variant-selects');
      const group = Array.from(picker?.children || []).filter(child => child.classList.contains('product-form__input'))[0];
      const select = group?.querySelector('select');
      return select ? select.selectedIndex === 0 : Array.from(group?.querySelectorAll('input[type="radio"]') || []).findIndex(input => input.checked) === 0;
    }
    previewTargets() {
      const ids = new Set((this.config.previewMediaIds || []).map(String));
      if (!ids.size) return [];
      const targets = new Map();
      const collect = node => {
        if (!node || node.closest?.('[data-lens-clone]')) return;
        const id = node.dataset?.mediaId;
        if (!ids.has(String(id))) return;
        const frames = Array.from(node.querySelectorAll('wallify-frame-preview')).filter(frame => !frame.closest('wallify-set-frame-preview, [data-lens-clone]'));
        if (frames.length) targets.set(node, frames);
      };
      this.root.querySelectorAll('gallery-carousel [data-media-id]').forEach(collect);
      this.root.querySelectorAll('gallery-carousel').forEach(gallery => {
        Array.from(gallery.originalSlides || []).forEach(collect);
        Array.from(gallery.originalThumbnails || []).forEach(collect);
      });
      return Array.from(targets, ([media, frames]) => ({ media, frames }));
    }
    loadingLabel() {
      if (typeof this.config.previewLoadingLabel === 'string' && this.config.previewLoadingLabel.trim()) return this.config.previewLoadingLabel;
      const labels = { it: 'Caricamento anteprima…', en: 'Loading preview…', fr: 'Chargement de l’aperçu…', de: 'Vorschau wird geladen…', es: 'Cargando vista previa…', pt: 'A carregar a pré-visualização…', nl: 'Voorbeeld laden…', da: 'Indlæser forhåndsvisning…', sv: 'Laddar förhandsvisning…', fi: 'Ladataan esikatselua…', no: 'Laster forhåndsvisning…', nb: 'Laster forhåndsvisning…', pl: 'Ładowanie podglądu…' };
      return labels[document.documentElement.lang.split('-')[0].toLowerCase()] || labels.en;
    }
    setLoading(surface, images, loading, compact = false) {
      if (!surface) return;
      const owner = this.constructor.loadingOwners.get(surface);
      if (loading && owner && owner !== this) owner.setLoading(surface, [], false);
      let record = this.loadingSurfaces.get(surface);
      if (!loading) {
        if (!record) return;
        record.images.forEach((style, image) => {
          if (style.value) image.style.setProperty('visibility', style.value, style.priority); else image.style.removeProperty('visibility');
        });
        if (record.positionChanged) {
          if (record.position) surface.style.setProperty('position', record.position, record.positionPriority); else surface.style.removeProperty('position');
        }
        if (record.busy === null) surface.removeAttribute('aria-busy'); else surface.setAttribute('aria-busy', record.busy);
        record.overlay.remove();
        this.loadingSurfaces.delete(surface);
        if (this.constructor.loadingOwners.get(surface) === this) this.constructor.loadingOwners.delete(surface);
        return;
      }
      if (!record) {
        const overlay = document.createElement('span');
        overlay.className = 'wallify-personalization-preview-loading';
        overlay.setAttribute('role', 'status');
        overlay.setAttribute('aria-label', this.loadingLabel());
        overlay.textContent = compact ? '…' : this.loadingLabel();
        overlay.style.cssText = 'position:absolute;inset:0;z-index:4;display:flex;align-items:center;justify-content:center;box-sizing:border-box;padding:12px;background:#fffdf9;color:#74675b;font-size:14px;line-height:1.4;text-align:center;pointer-events:none;';
        const position = surface.style.getPropertyValue('position');
        const positionPriority = surface.style.getPropertyPriority('position');
        const positionChanged = !getComputedStyle(surface).position || getComputedStyle(surface).position === 'static';
        record = { overlay, images: new Map(), position, positionPriority, positionChanged, busy: surface.getAttribute('aria-busy') };
        this.loadingSurfaces.set(surface, record);
        this.constructor.loadingOwners.set(surface, this);
        if (positionChanged) surface.style.setProperty('position', 'relative');
        surface.setAttribute('aria-busy', 'true');
        surface.append(overlay);
      }
      images.forEach(image => {
        if (!record.images.has(image)) record.images.set(image, { value: image.style.getPropertyValue('visibility'), priority: image.style.getPropertyPriority('visibility') });
        image.style.setProperty('visibility', 'hidden', 'important');
      });
    }
    canRetainPreview(targets) {
      // Loading is requested only after the selected mapped source passes validation.
      return targets.some(({ images, frames }) => {
        const artworkImages = images || frames?.flatMap(frame => Array.from(frame.querySelectorAll('img.wallify-frame-preview__artwork'))) || [];
        return artworkImages.length > 0;
      });
    }
    setPreviewAvailability(available, { loading = false } = {}) {
      let changed = false;
      const targets = this.previewTargets();
      const retain = loading && this.canRetainPreview(targets);
      const value = retain ? 'pending' : String(available);
      const frames = new Set();
      targets.forEach(({ media, frames: targetFrames }) => {
        targetFrames.forEach(frame => frames.add(frame));
        if (media.dataset.wallifyDesignAvailable !== value) { media.dataset.wallifyDesignAvailable = value; changed = true; }
        const pending = String(retain);
        if (media.dataset.wallifyDesignLoading !== pending) media.dataset.wallifyDesignLoading = pending;
        const fallback = this.config.previewFallbackMediaId;
        if (fallback && media.dataset.wallifyDesignFallbackMediaId !== String(fallback)) media.dataset.wallifyDesignFallbackMediaId = String(fallback);
      });
      this.root.querySelectorAll('wallify-frame-preview[data-wallify-inline-preview]').forEach(frame => {
        if (frame.closest('wallify-set-frame-preview, [data-lens-clone]')) return;
        frames.add(frame);
        if (frame.dataset.wallifyDesignAvailable !== value) frame.dataset.wallifyDesignAvailable = value;
      });
      frames.forEach(frame => {
        const surface = frame.querySelector('.wallify-frame-preview__picture');
        const images = Array.from(surface?.querySelectorAll('img.wallify-frame-preview__artwork') || []);
        this.setLoading(surface, images, retain, frame.classList.contains('wallify-frame-preview--thumbnail') || frame.hasAttribute('data-wallify-inline-preview'));
      });
      if (changed) this.root.dispatchEvent(new CustomEvent('wallify:design-preview-change', { bubbles: true }));
    }
    posterPreviewTargets() {
      const ids = new Set((this.config.posterPreviewMediaIds || []).map(String));
      if (!ids.size) return [];
      const targets = new Map();
      const collect = node => {
        if (!node || node.closest?.('[data-lens-clone]') || !ids.has(String(node.dataset?.mediaId))) return;
        if (node.querySelector('wallify-frame-preview, wallify-set-frame-preview')) return;
        const images = Array.from(node.querySelectorAll('img')).filter(image => !image.closest('[data-lens-clone]'));
        if (images.length) targets.set(node, images);
      };
      this.root.querySelectorAll('gallery-carousel [data-media-id]').forEach(collect);
      this.root.querySelectorAll('gallery-carousel').forEach(gallery => {
        Array.from(gallery.originalSlides || []).forEach(collect);
        Array.from(gallery.originalThumbnails || []).forEach(collect);
      });
      return Array.from(targets, ([media, images]) => ({ media, images }));
    }
    setPosterAvailability(available, { loading = false } = {}) {
      let changed = false;
      const targets = this.posterPreviewTargets();
      const retain = loading && this.canRetainPreview(targets);
      const value = retain ? 'pending' : String(available);
      targets.forEach(({ media, images }) => {
        if (media.dataset.wallifyPosterPreview !== 'true') { media.dataset.wallifyPosterPreview = 'true'; changed = true; }
        if (media.dataset.wallifyDesignAvailable !== value) { media.dataset.wallifyDesignAvailable = value; changed = true; }
        const pending = String(retain);
        if (media.dataset.wallifyDesignLoading !== pending) media.dataset.wallifyDesignLoading = pending;
        const fallback = this.config.previewFallbackMediaId;
        if (fallback && media.dataset.wallifyDesignFallbackMediaId !== String(fallback)) media.dataset.wallifyDesignFallbackMediaId = String(fallback);
        images.forEach(image => this.setLoading(image.parentElement, [image], retain, !image.closest('.product__media')));
      });
      if (changed) this.root.dispatchEvent(new CustomEvent('wallify:design-preview-change', { bubbles: true }));
    }
    loadArtwork(artwork) {
      const cached = this.constructor.artworkLoads.get(artwork.src);
      if (cached) return cached;
      const load = { status: 'pending' };
      load.promise = new Promise(resolve => {
        const image = new Image();
        const finish = success => { load.status = success ? 'ready' : 'failed'; resolve(success); };
        image.onload = () => finish(image.naturalWidth > 0 && image.naturalHeight > 0);
        image.onerror = () => finish(false);
        image.src = artwork.src;
        if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) finish(true);
      });
      this.constructor.artworkLoads.set(artwork.src, load);
      return load;
    }
    decodeImages(images) {
      const decodes = images.map(image => {
        if (typeof image.decode !== 'function') return { status: 'ready', promise: Promise.resolve(true) };
        const signature = image.dataset.wallifyDesignSource;
        const cached = this.constructor.imageDecodes.get(image);
        if (cached?.signature === signature) return cached;
        const decode = { signature, status: 'pending' };
        decode.promise = Promise.resolve().then(() => image.decode()).then(() => {
          const ready = image.dataset.wallifyDesignSource === signature;
          decode.status = ready ? 'ready' : 'failed';
          return ready;
        }, () => { decode.status = 'failed'; return false; });
        this.constructor.imageDecodes.set(image, decode);
        return decode;
      });
      return {
        status: decodes.some(decode => decode.status === 'failed') ? 'failed' : decodes.every(decode => decode.status === 'ready') ? 'ready' : 'pending',
        promise: Promise.all(decodes.map(decode => decode.promise)).then(results => results.every(Boolean))
      };
    }
    prepareDesign(record, value) {
      const artwork = this.artworkFor(record, value);
      if (!artwork) return { status: 'failed', promise: Promise.resolve(false) };
      const frames = new Set(this.previewTargets().flatMap(target => target.frames));
      this.root.querySelectorAll('wallify-frame-preview[data-wallify-inline-preview]').forEach(frame => {
        if (!frame.closest('wallify-set-frame-preview, [data-lens-clone]')) frames.add(frame);
      });
      const signature = artwork.src + '|' + artwork.width + '|' + artwork.height;
      const images = [];
      frames.forEach(frame => {
        const original = frame.querySelector('img.wallify-frame-preview__artwork');
        if (!original) return;
        let prepared = this.constructor.preparedArtworks.get(frame);
        if (!prepared) this.constructor.preparedArtworks.set(frame, prepared = new Map());
        let image = prepared.get(signature);
        if (!image) {
          image = original.cloneNode(false);
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          ['width', 'height', 'left', 'right', 'top', 'bottom', 'position', 'object-fit', 'transform', 'max-width', 'max-height', 'visibility'].forEach(property => image.style.removeProperty(property));
          image.width = artwork.width;
          image.height = artwork.height;
          image.loading = 'eager';
          image.src = artwork.src;
          image.dataset.wallifyDesignSource = signature;
          prepared.set(signature, image);
        }
        images.push(image);
      });
      return this.decodeImages(images);
    }
    prepareDesignChoices() {
      this.records.forEach(record => {
        if (!record.select || !record.spec.designArtworks) return;
        this.prepareDesign(record, record.select.value);
      });
    }
    prepareFramedSelection() {
      const record = Array.from(this.records.values()).find(record => record.select && record.spec.designArtworks);
      return record ? this.prepareDesign(record, record.select.value) : { status: 'ready', promise: Promise.resolve(true) };
    }
    applyArtwork(artwork, value) {
      const images = [];
      const frames = new Set(this.previewTargets().flatMap(target => target.frames));
      this.root.querySelectorAll('wallify-frame-preview[data-wallify-inline-preview]').forEach(frame => {
        if (!frame.closest('wallify-set-frame-preview, [data-lens-clone]')) frames.add(frame);
      });
      frames.forEach(frame => {
        const picture = frame.querySelector('.wallify-frame-preview__picture');
        let image = picture?.querySelector('img.wallify-frame-preview__artwork');
        if (!image) return;
        const signature = artwork.src + '|' + artwork.width + '|' + artwork.height;
        const prepared = this.constructor.preparedArtworks.get(frame)?.get(signature);
        if (prepared && this.constructor.imageDecodes.get(prepared)?.status === 'ready' && image !== prepared) {
          this.setLoading(picture, [], false);
          image.replaceWith(prepared);
          image = prepared;
        }
        images.push(image);
        if (image.dataset.wallifyDesignSource !== signature) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.closest('picture')?.querySelectorAll('source').forEach(source => { source.removeAttribute('srcset'); source.removeAttribute('sizes'); });
          ['width', 'height', 'left', 'right', 'top', 'bottom', 'position', 'object-fit', 'transform', 'max-width', 'max-height'].forEach(property => image.style.removeProperty(property));
          image.width = artwork.width;
          image.height = artwork.height;
          image.src = artwork.src;
          image.dataset.wallifyDesignSource = signature;
        }
        if (image.dataset.wallifyDesignValue !== value) image.dataset.wallifyDesignValue = value;
      });
      return images;
    }
    applyPosterMockup(mockup, value) {
      const appliedImages = [];
      const signature = mockup.src + '|' + mockup.width + '|' + mockup.height;
      this.posterPreviewTargets().forEach(({ media, images }) => {
        images.forEach(image => {
          appliedImages.push(image);
          if (image.dataset.wallifyDesignSource !== signature) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.closest('picture')?.querySelectorAll('source').forEach(source => { source.removeAttribute('srcset'); source.removeAttribute('sizes'); });
            ['width', 'height', 'left', 'right', 'top', 'bottom', 'position', 'object-fit', 'object-position', 'transform', 'max-width', 'max-height'].forEach(property => image.style.removeProperty(property));
            image.width = mockup.width;
            image.height = mockup.height;
            image.src = mockup.src;
            image.classList.add('wallify-personalization-poster-image');
            image.dataset.wallifyDesignSource = signature;
          }
          const responsive = image.closest('responsive-image');
          if (responsive) {
            responsive.originalSrcset = mockup.src;
            responsive.intersectionObserver?.unobserve(image);
            responsive.resizeObserver?.unobserve(image);
          }
          if (image.dataset.wallifyDesignValue !== value) image.dataset.wallifyDesignValue = value;
        });
        media.querySelectorAll('a[data-pswp-src]').forEach(link => {
          if (link.getAttribute('data-pswp-src') !== mockup.src) {
            link.href = mockup.src;
            link.setAttribute('data-pswp-src', mockup.src);
            link.setAttribute('data-pswp-width', String(mockup.width));
            link.setAttribute('data-pswp-height', String(mockup.height));
            link.setAttribute('data-cropped', 'false');
          }
        });
      });
      return appliedImages;
    }
    navigatePreview(fallback = false) {
      if (!this.framedSelected()) return;
      const gallery = this.root.querySelector('gallery-carousel');
      const id = fallback ? this.config.previewFallbackMediaId : this.config.previewMediaIds?.[0];
      if (id && gallery && typeof gallery.updateCarouselImages === 'function') gallery.updateCarouselImages(String(id));
    }
    navigatePosterPreview(fallback = false) {
      if (!this.posterSelected()) return;
      const gallery = this.root.querySelector('gallery-carousel');
      const id = fallback ? this.config.previewFallbackMediaId : this.config.posterPreviewMediaIds?.[0];
      if (id && gallery && typeof gallery.updateCarouselImages === 'function') gallery.updateCarouselImages(String(id));
    }
    updateDesignPreview(options = {}) {
      this.updateFramedDesignPreview(options);
      this.updatePosterPreview(options);
    }
    updateFramedDesignPreview({ navigate = false } = {}) {
      if (!this.root || !this.isConnected || !this.config.previewMediaIds?.length) return;
      const record = Array.from(this.records.values()).find(record => record.select && Object.prototype.hasOwnProperty.call(record.spec, 'designArtworks'));
      if (!record) return;
      const value = record.select.value;
      const artwork = this.artworkFor(record, value);
      const signature = value + '|' + (artwork ? artwork.src + '|' + artwork.width + '|' + artwork.height : 'missing');
      if (signature !== this.previewSignature) {
        this.previewSignature = signature;
        this.previewToken++;
        this.previewPending = null;
        this.previewNavigate = false;
      }
      if (!artwork) {
        this.setPreviewAvailability(false);
        if (navigate) this.navigatePreview(true);
        return;
      }
      let load = this.prepareDesign(record, value);
      if (load.status === 'ready') load = this.decodeImages(this.applyArtwork(artwork, value));
      if (load.status === 'ready') {
        this.setPreviewAvailability(true);
        if (navigate) this.navigatePreview();
        return;
      }
      if (load.status === 'failed') {
        this.setPreviewAvailability(false);
        if (navigate) this.navigatePreview(true);
        return;
      }
      this.setPreviewAvailability(false, { loading: true });
      if (navigate && this.framedSelected()) this.previewNavigate = true;
      if (this.previewPending === signature) return;
      this.previewPending = signature;
      const token = this.previewToken;
      load.promise.then(() => {
        if (!this.isConnected || token !== this.previewToken || this.previewSignature !== signature) return;
        const requestedNavigation = this.previewNavigate;
        this.previewPending = null;
        this.previewNavigate = false;
        this.updateDesignPreview({ navigate: requestedNavigation });
      });
    }
    updatePosterPreview({ navigate = false } = {}) {
      if (!this.root || !this.isConnected || !this.config.posterPreviewMediaIds?.length) return;
      const record = Array.from(this.records.values()).find(record => record.select && Object.prototype.hasOwnProperty.call(record.spec, 'posterMockups'));
      if (!record) return;
      const value = record.select.value;
      const mockup = this.posterMockupFor(record, value);
      const signature = value + '|' + (mockup ? mockup.src + '|' + mockup.width + '|' + mockup.height : 'missing');
      if (signature !== this.posterSignature) {
        this.posterSignature = signature;
        this.posterToken++;
        this.posterPending = null;
        this.posterNavigate = false;
      }
      if (!mockup) {
        this.setPosterAvailability(false);
        if (navigate) this.navigatePosterPreview(true);
        return;
      }
      let load = this.loadArtwork(mockup);
      if (load.status === 'ready') load = this.decodeImages(this.applyPosterMockup(mockup, value));
      if (load.status === 'ready') {
        this.setPosterAvailability(true);
        if (navigate) this.navigatePosterPreview();
        return;
      }
      if (load.status === 'failed') {
        this.setPosterAvailability(false);
        if (navigate) this.navigatePosterPreview(true);
        return;
      }
      this.setPosterAvailability(false, { loading: true });
      if (navigate && this.posterSelected()) this.posterNavigate = true;
      if (this.posterPending === signature) return;
      this.posterPending = signature;
      const token = this.posterToken;
      load.promise.then(() => {
        if (!this.isConnected || token !== this.posterToken || this.posterSignature !== signature) return;
        const requestedNavigation = this.posterNavigate;
        this.posterPending = null;
        this.posterNavigate = false;
        this.updateDesignPreview({ navigate: requestedNavigation });
      });
    }
    disconnectedCallback() {
      if (this.relocating || !this.controller) return;
      this.controller.abort();
      this.observer?.disconnect();
      if (this.task) cancelAnimationFrame(this.task);
      this.task = null;
      this.previewToken++;
      this.previewPending = null;
      this.previewNavigate = false;
      this.posterToken++;
      this.posterPending = null;
      this.posterNavigate = false;
      if (this.galleryMain && this.galleryMoveHandler) this.galleryMain.off('move.wallifyPersonalization');
      this.galleryMain = null;
      this.galleryMoveHandler = null;
      Array.from(this.loadingSurfaces.keys()).forEach(surface => this.setLoading(surface, [], false));
      const root = this.root;
      const origin = this.origin;
      this.records.forEach(record => {
        this.remember(record);
        if (record.select) {
          record.field.hidden = record.wasHidden;
          record.field.classList.remove('wallify-personalization-native-field');
          record.view.remove();
        } else if (record.anchor?.isConnected && root?.isConnected) {
          record.anchor.after(record.field);
          record.anchor.remove();
        }
      });
      this.records.clear();
      this.controller = null;
      this.hidden = true;
      if (root?.isConnected && origin?.isConnected) queueMicrotask(() => {
        if (!this.isConnected && root.isConnected && origin.isConnected) origin.after(this);
      });
    }
  });
})();


